import React, { Suspense, lazy, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import HomePage from "./pages/HomePage";
import ProductPage from "./pages/ProductPage";
import CategoryPage from "./pages/CategoryPage";
import CartPage from "./pages/CartPage";
import SearchResultsPage from "./pages/SearchResultsPage";
import { CartProvider } from "./contexts/CartContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { AuthProvider } from "./contexts/AuthContext";
import Services from "./pages/ServicesPage";
import Maintenance from "./pages/Maintenance";
import Register from "./pages/RegisterPage";
import Login from "./pages/LoginPage";
import PCConfigsPage from "./pages/PCConfigsPage";
import PCConfigDetailPage from "./pages/PCConfigDetailPage";
import AdminRoute from "./components/common/AdminRoute";

// Off the storefront path: checkout, account and admin pages, and pages
// reached from an email or the footer. Each loads on first visit.
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AdminOrdersPage = lazy(() => import("./pages/AdminOrdersPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const AdminPCConfigsPage = lazy(() => import("./pages/AdminPCConfigsPage"));
const AdminHomepageSettingsPage = lazy(() => import("./pages/AdminHomepageSettingsPage"));
const AdminNewsletterPage = lazy(() => import("./pages/AdminNewsletterPage"));
const AdminSlugsPage = lazy(() => import("./pages/AdminSlugsPage"));
const AdminCategoryMappingsPage = lazy(() => import("./pages/AdminCategoryMappingsPage"));
const AdminDuplicatesPage = lazy(() => import("./pages/AdminDuplicatesPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsAndConditionsPage = lazy(() => import("./pages/TermsAndConditionsPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentCancelledPage = lazy(() => import("./pages/PaymentCancelledPage"));

const MAINTENANCE_MODE = true;
const MAINTENANCE_PASSWORD = "gormiti1";

// Create a client 
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Page paths must not match a backend GET route: on a direct load the API
// answers first and the browser gets JSON (why /search and /pc-configs moved).
const LegacyPCConfigRedirect: React.FC = () => {
  const { id } = useParams();
  return <Navigate to={`/konfiguratsii/${id}`} replace />;
};

const App: React.FC = () => {
  const [hasAdminAccess, setHasAdminAccess] = useState(false);

  if (MAINTENANCE_MODE && !hasAdminAccess) {
    return (
      <Maintenance
        password={MAINTENANCE_PASSWORD}
        onAdminAccess={() => setHasAdminAccess(true)}
      />
    );
  }

  return (
    <AuthProvider>
      <NotificationProvider>
        <CartProvider>
          <Router basename={process.env.NODE_ENV === "production" ? "/" : "/"}>
            {MAINTENANCE_MODE && hasAdminAccess && (
              <div className="bg-green-700 text-white text-center py-3 px-4 text-sm font-medium shadow-sm">
                Сайтът е в режим на поддръжка - видим е само за администратори
                <button
                  onClick={() => setHasAdminAccess(false)}
                  className="ml-4 bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full text-xs transition-colors"
                >
                  Изход от админ режим
                </button>
              </div>
            )}

            <Suspense fallback={<div className="flex justify-center py-16 text-lg">Зареждане...</div>}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/product/:slug" element={<ProductPage />} />
                <Route path="/category/:slug" element={<CategoryPage />} />
                <Route path="/parameters/:id" element={<CategoryPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/tarsene" element={<SearchResultsPage />} />
                <Route path="/services" element={<Services />} />
                <Route path="/konfiguratsii" element={<PCConfigsPage />} />
                <Route path="/konfiguratsii/:id" element={<PCConfigDetailPage />} />
                {/* Old paths, kept for links saved before the rename (e.g. hero slide URLs) */}
                <Route path="/pc-configs" element={<Navigate to="/konfiguratsii" replace />} />
                <Route path="/pc-configs/:id" element={<LegacyPCConfigRedirect />} />
                <Route path="/register" element={<Register />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsAndConditionsPage />} />
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/payment/cancelled" element={<PaymentCancelledPage />} />
                <Route path="/payment/error" element={<PaymentCancelledPage />} />
                <Route path="/admin" element={
                  <AdminRoute>
                    <AdminDashboardPage />
                  </AdminRoute>
                } />
                <Route path="/admin/orders" element={
                  <AdminRoute>
                    <AdminOrdersPage />
                  </AdminRoute>
                } />
                <Route path="/admin/configs" element={
                  <AdminRoute>
                    <AdminPCConfigsPage />
                  </AdminRoute>
                } />
                <Route path="/admin/settings/homepage" element={
                  <AdminRoute>
                    <AdminHomepageSettingsPage />
                  </AdminRoute>
                } />
                <Route path="/admin/newsletter" element={
                  <AdminRoute>
                    <AdminNewsletterPage />
                  </AdminRoute>
                } />
                <Route path="/admin/slugs" element={
                  <AdminRoute>
                    <AdminSlugsPage />
                  </AdminRoute>
                } />
                <Route path="/admin/mappings" element={
                  <AdminRoute>
                    <AdminCategoryMappingsPage />
                  </AdminRoute>
                } />
                <Route path="/admin/product-duplicates" element={
                  <AdminRoute>
                    <AdminDuplicatesPage />
                  </AdminRoute>
                } />
                {/* 404 страница */}
                <Route
                  path="*"
                  element={
                    <div className="flex items-center justify-center min-h-screen bg-gray-100">
                      <div className="text-center">
                        <h2 className="text-2xl font-bold mb-4">
                          Страницата не е намерена
                        </h2>
                        <p className="text-gray-600 mb-6">
                          Страницата, която търсите, не съществува или е преместена.
                        </p>
                        <a
                          href="/"
                          className="bg-[#31B823] hover:bg-[#28A01D] text-white font-medium py-2 px-6 rounded transition-colors"
                        >
                          Обратно към началната страница
                        </a>
                      </div>
                    </div>
                  }
                />
              </Routes>
            </Suspense>
          </Router>
        </CartProvider>
      </NotificationProvider>
    </AuthProvider>
  );
};

export default App;